/**
 * WAFW Frontend Error Monitoring & Auto-Recovery System
 * =====================================================
 * Catches all frontend errors, reports them to backend, and attempts recovery.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

// Error severity levels
const ErrorSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Error categories
const ErrorCategory = {
  REACT: 'react',
  API: 'api',
  NETWORK: 'network',
  VALIDATION: 'validation',
  RENDER: 'render',
  UNKNOWN: 'unknown'
};

// In-memory error cache for deduplication
const errorCache = new Map();
const ERROR_CACHE_TTL = 60000; // 1 minute

/**
 * Check if error is from external source (browser extensions, third-party scripts)
 */
function isExternalError(error, context = {}) {
  const message = error.message || String(error);
  const stack = error.stack || '';
  const source = context.source || '';
  
  // List of known external error patterns to ignore
  const externalPatterns = [
    // Browser extensions
    /background\.js/i,
    /content\.js/i,
    /chrome-extension/i,
    /moz-extension/i,
    /extension:/i,
    /classifier\.js/i,
    // Third-party ad/marketing scripts
    /rokt/i,
    /rokt-icons/i,
    /apps\.rokt\.com/i,
    /googlesyndication/i,
    /google-analytics/i,
    /doubleclick/i,
    /facebook\.net/i,
    /fb\.com/i,
    /hotjar/i,
    /clarity\.ms/i,
    // Common browser extension errors
    /No tab with id/i,
    /message channel closed/i,
    /A listener indicated an asynchronous response/i,
    /preloaded using link preload but not used/i,
    /preload.*not used/i,
    // Generic extension patterns
    /inpage\.js/i,
    /inject\.js/i,
    // Leaflet map errors during unmount (expected)
    /Cannot read properties of undefined \(reading 'classList'\)/i,
    /DomUtil/i,
    // Service worker errors
    /Request scheme 'chrome-extension' is unsupported/i,
    /Cache.*unsupported/i,
    // Internal React errors that are expected
    /ResizeObserver loop/i,
    // 404 errors for new endpoints on production (expected until redeploy)
    /contributors\/all/i,
    /moderation\/dashboard/i,
    // Transient network errors from polling (expected when offline)
    /Network Error/i,
    /Error polling venues/i,
    /AxiosError.*Network/i,
    /net::ERR_/i,
    /ERR_NAME_NOT_RESOLVED/i,
    /Failed to fetch/i,
    /Failed to load resource/i,
    // React cleanup errors (harmless)
    /removeEventListener is not a function/i,
    /Cannot read properties of null/i,
    /unmounted component/i,
    // CORS errors for external resources
    /CORS/i,
    /cross-origin/i,
    // Third-party advertising/tracking scripts
    /rokt\.com/i,
    /apps\.rokt/i,
    /Content Security Policy/i,
    /script-src.*self/i,
    /Loading the script.*blob/i,
    // Minified code errors from third-party scripts
    /this\.o\.at is not a function/i,
    /\.o\.at is not a function/i,
    /Blob is not a constructor/i,
    // Frame errors
    /Frame with ID.*removed/i,
    // Cloudflare RUM errors
    /cdn-cgi/i,
    /cdn-cgi\/rum/i,
    // Emergent platform tracking (expected CORS on production domain)
    /emergent\.host/i,
    /track\/session/i,
    /ERR_FAILED/i,
    /Access-Control-Allow-Credentials/i,
    // WebAssembly errors from browser extensions
    /WebAssembly\.instantiate/i,
    /RuntimeError.*Aborted/i,
    /CompileError.*WebAssembly/i,
    /sASSERTIONS/i,
    // Shadow DOM errors from browser extensions
    /CssSelectorGenerator/i,
    /shadow root inferred/i,
    /ShadowRoot/i,
    /Shadow DOM/i,
    // Geolocation errors (user denied permission - expected behavior)
    /GeolocationPositionError/i,
    /Geolocation error/i,
    /User denied Geolocation/i,
    /Permission denied.*geolocation/i,
    /Position unavailable/i,
    // 404 errors on owner/dashboard lookups (invalid/deleted IDs - not a bug)
    /Error loading owner data.*404/i,
    /owner.*404/i,
    /dashboard\?owner=.*404/i,
    // 404 errors on venue lookups (invalid venue IDs)
    /venue.*not found/i,
    /Venue.*404/i,
    // Authentication expected failures (logout, expired token)
    /401.*Unauthorized/i,
    /403.*Forbidden/i
  ];
  
  const allText = `${message} ${stack} ${source}`;
  return externalPatterns.some(pattern => pattern.test(allText));
}

/**
 * Report error to backend
 */
async function reportError(error, context = {}) {
  // Skip external errors (browser extensions, third-party scripts)
  if (isExternalError(error, context)) {
    console.debug('[ErrorMonitor] Skipping external error:', error.message?.substring(0, 100));
    return;
  }
  
  const errorData = {
    message: error.message || String(error),
    stack: error.stack,
    component: context.component,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    category: context.category || ErrorCategory.UNKNOWN,
    severity: context.severity || ErrorSeverity.MEDIUM,
    ...context
  };

  // Deduplicate errors
  const errorKey = `${errorData.message}-${errorData.component}`;
  if (errorCache.has(errorKey)) {
    const cached = errorCache.get(errorKey);
    if (Date.now() - cached.time < ERROR_CACHE_TTL) {
      cached.count++;
      console.warn(`[ErrorMonitor] Duplicate error suppressed (${cached.count}x):`, errorData.message);
      return;
    }
  }
  errorCache.set(errorKey, { time: Date.now(), count: 1 });

  // Log to console
  console.error('[ErrorMonitor] Captured error:', errorData);

  // Report to backend
  try {
    await fetch(`${API}/api/system/errors/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorData)
    });
  } catch (e) {
    console.error('[ErrorMonitor] Failed to report error to backend:', e);
  }
}

/**
 * Auto-retry wrapper for API calls
 */
async function withAutoRetry(apiCall, options = {}) {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    onRetry = null,
    context = 'API call'
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await apiCall();
      
      // Log recovery if this was a retry
      if (attempt > 0) {
        console.log(`[AutoRecovery] ${context} succeeded after ${attempt} retries`);
      }
      
      return result;
    } catch (error) {
      lastError = error;

      // Check if error is retryable
      const isRetryable = isRetryableError(error);

      if (isRetryable && attempt < maxRetries) {
        const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
        console.warn(`[AutoRecovery] Retrying ${context} (${attempt + 1}/${maxRetries}) in ${delay}ms:`, error.message);
        
        if (onRetry) {
          onRetry(attempt + 1, maxRetries, error);
        }
        
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Non-retryable or max retries reached
      await reportError(error, {
        category: ErrorCategory.API,
        severity: ErrorSeverity.HIGH,
        component: context,
        retryAttempts: attempt
      });

      throw error;
    }
  }

  throw lastError;
}

/**
 * Check if error is retryable
 */
function isRetryableError(error) {
  // Network errors
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return true;
  }
  
  // Timeout errors
  if (error.message?.toLowerCase().includes('timeout')) {
    return true;
  }

  // HTTP status codes that are retryable
  if (error.response?.status) {
    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    return retryableStatuses.includes(error.response.status);
  }

  // Axios network errors
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    return true;
  }

  return false;
}

/**
 * Setup global error handlers
 */
function setupGlobalErrorHandlers() {
  // Catch unhandled JavaScript errors
  window.onerror = (message, source, lineno, colno, error) => {
    const errorObj = error || new Error(message);
    const context = {
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.HIGH,
      component: 'window.onerror',
      source,
      lineno,
      colno
    };
    
    // Skip external errors silently
    if (isExternalError(errorObj, context)) {
      return false;
    }
    
    reportError(errorObj, context);
    return false; // Let default handler run too
  };

  // Catch unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    // Skip external errors silently
    if (isExternalError(error)) {
      return;
    }
    
    reportError(error, {
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.HIGH,
      component: 'unhandledrejection'
    });
  };

  // Catch console errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Don't report our own error logs
    if (args[0]?.includes?.('[ErrorMonitor]')) {
      originalConsoleError.apply(console, args);
      return;
    }

    // Report significant console errors
    const message = args.map(a => String(a)).join(' ');
    if (message.length > 10 && !message.includes('Warning:')) {
      reportError(new Error(message), {
        category: ErrorCategory.UNKNOWN,
        severity: ErrorSeverity.LOW,
        component: 'console.error'
      });
    }

    originalConsoleError.apply(console, args);
  };

  console.log('[ErrorMonitor] Global error handlers installed');
}

/**
 * Get system health status
 */
async function getSystemHealth() {
  try {
    const response = await fetch(`${API}/api/system/health`);
    return await response.json();
  } catch (e) {
    return { healthy: false, error: e.message };
  }
}

/**
 * Get error statistics
 */
async function getErrorStats() {
  try {
    const response = await fetch(`${API}/api/system/errors/stats`);
    return await response.json();
  } catch (e) {
    return { error: e.message };
  }
}

// Export utilities
export {
  reportError,
  withAutoRetry,
  isRetryableError,
  isExternalError,
  setupGlobalErrorHandlers,
  getSystemHealth,
  getErrorStats,
  ErrorSeverity,
  ErrorCategory
};

// Auto-setup on import
if (typeof window !== 'undefined') {
  setupGlobalErrorHandlers();
}
